import React, { createContext, useState, useEffect, ReactNode, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { handleTokenRefresh } from '../services/ApiService';
import { startRealtimeConnection, stopRealtimeConnection } from '../services/RealtimeService';

interface ITokenPayload {
  sub: string;
  email: string;
  exp: number;
  // Per-application access-control role (Admin/User/...), separate from the
  // membership-tier claim. See AuthorizationApi's TokenService/Program.cs.
  AppRole?: string;
}

interface AuthContextProps {
  isAuthenticated: boolean;
  userName: string | null;
  userId: string | null;
  isAdmin: boolean;
  // "Moderator" -- second, more limited admin-panel role (see AdminRoles on
  // the backend): same day-to-day moderation, but never role changes and
  // never granting another Admin. isAdminOrModerator is the one to use for
  // "can this user see/enter the admin panel at all" checks.
  isModerator: boolean;
  isAdminOrModerator: boolean;
  loading: boolean;
  logout: () => void;
  login: (token: string, refreshToken: string) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const refreshingRef = useRef(false);

  useEffect(() => {
    const verifyAndRefreshToken = async (retryCount = 0) => {
      const MAX_RETRIES = 1;
      const token = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");

      if (token) {
        try {
          const decodedToken = jwtDecode<ITokenPayload>(token);

          if (decodedToken.exp * 1000 > Date.now()) {
            // ✅ Token válido
            setIsAuthenticated(true);
            setUserName(decodedToken.email);
            setUserId(decodedToken.sub);
            setIsAdmin(decodedToken.AppRole === 'Admin');
            setIsModerator(decodedToken.AppRole === 'Moderator');
            setLoading(false);
          } else if (refreshToken && retryCount < MAX_RETRIES && !refreshingRef.current) {
            // 🔄 Token expirado → Intentar refresh
            refreshingRef.current = true;

            const refreshSuccess = await handleTokenRefresh();

            refreshingRef.current = false;

            if (refreshSuccess) {
              // ✅ Refresh exitoso → Actualizar estado
              const newToken = localStorage.getItem("accessToken");
              if (newToken) {
                const newDecodedToken = jwtDecode<ITokenPayload>(newToken);
                setIsAuthenticated(true);
                setUserName(newDecodedToken.email);
                setUserId(newDecodedToken.sub);
                setIsAdmin(newDecodedToken.AppRole === 'Admin');
                setIsModerator(newDecodedToken.AppRole === 'Moderator');
                setLoading(false);
              }
            } else {
              // ❌ Refresh falló → Logout
              manageLogout();
            }
          } else {
            // ❌ Token expirado sin refresh o max retries → Logout
            manageLogout();
          }
        } catch (err) {
          console.error("Invalid token:", err);
          manageLogout();
        }
      } else {
        // Sin token → Primera visita o logout manual
        setLoading(false);
        // ✅ NO hacer navigate aquí
        // ProtectedRoute se encargará del redirect si es necesario
      }
    };

    verifyAndRefreshToken();
  }, [location.pathname]);

  // Single shared SignalR connection (chat + notification push) for the
  // whole app -- started once signed in, stopped on logout, mirroring
  // Authentication76's AuthProvider/RealtimeService wiring.
  useEffect(() => {
    if (isAuthenticated) startRealtimeConnection();
    else stopRealtimeConnection();
  }, [isAuthenticated]);

  /**
   * manageLogout - Limpia el estado de autenticación
   *
   * IMPORTANTE: NO hace navigate('/login') porque:
   * - ProtectedRoute se encarga del redirect en rutas protegidas
   * - Evita conflictos y dobles redirects
   */
  const manageLogout = () => {
    localStorage.clear(); // Esto también limpia redirectAfterLogin
    setIsAuthenticated(false);
    setUserName(null);
    setUserId(null);
    setIsAdmin(false);
    setIsModerator(false);
    setLoading(false);
    refreshingRef.current = false;

    // ✅ ProtectedRoute manejará el redirect
    // NO hacer navigate aquí para evitar conflictos
  };

  /**
   * handleLogout - Logout manual por el usuario
   *
   * Este SÍ hace navigate porque es una acción explícita del usuario
   * (ej: click en botón "Logout")
   */
  const handleLogout = () => {
    console.log('User logged out');
    localStorage.clear(); // Limpia todo, incluyendo redirectAfterLogin
    setIsAuthenticated(false);
    setUserName(null);
    setUserId(null);
    setIsAdmin(false);
    setIsModerator(false);
    refreshingRef.current = false;
    navigate('/login'); // ✅ Redirect explícito OK aquí
  };

  /**
   * login - Maneja el login del usuario
   *
   * Después de autenticar, redirige a:
   * 1. La URL que el usuario quería visitar (si existe en localStorage)
   * 2. La home "/" (por defecto) -- misma página con o sin sesión, ver Landing.tsx
   */
  const login = (token: string, refreshToken: string) => {
    try {
      const decodedToken = jwtDecode<ITokenPayload>(token);
      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userName', decodedToken.email);
      localStorage.setItem('userId', decodedToken.sub);

      setIsAuthenticated(true);
      setUserName(decodedToken.email);
      setUserId(decodedToken.sub);
      setIsAdmin(decodedToken.AppRole === 'Admin');
      setIsModerator(decodedToken.AppRole === 'Moderator');

      // 🎯 Verificar si hay una URL guardada para redirigir
      const redirectUrl = localStorage.getItem('redirectAfterLogin');

      if (redirectUrl) {
        // Limpiar la URL guardada antes de redirigir
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl);
      } else {
        // Comportamiento por defecto: la home (misma página, con o sin sesión)
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to decode token:', err);
      handleLogout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userName,
        userId,
        isAdmin,
        isModerator,
        isAdminOrModerator: isAdmin || isModerator,
        loading,
        logout: handleLogout,
        login,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
